import { Switch } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { itemCategoriesKey, useItemCategories, type ItemCategory } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { GarmentImage } from '@/components/GarmentImage';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { IconAction } from '@/components/IconAction';
import { StatusBadge } from '@/components/StatusBadge';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import type { Session } from '@core/session/types';

import { CategoryPage } from './categories/CategoryPage';

type Editing = { mode: 'create' } | { mode: 'edit'; category: ItemCategory };

/**
 * The category tree (spec Sections 7, 9, 14).
 *
 * Rendered as an indented flat table rather than an expandable tree. The server already returns
 * the rows in tree-walk order with a `depth`, so indentation is presentation and nothing here
 * re-derives the hierarchy — which also means the list stays sortable and filterable, whereas an
 * expand/collapse tree stops being either the moment it has forty rows.
 *
 * The screen's real job is making inheritance legible. A category that sets no turnaround buffer
 * is not "0 days", it is "whatever Ethnic Wear says", and showing a bare number would quietly
 * teach the shop the wrong thing about their own availability.
 */
export function CategoriesPage({ session }: { session: Session }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);

  const categories = useItemCategories(session.branch?.id, includeInactive);

  const remove = useApiMutation<void, ItemCategory>({
    mutationFn: (category) =>
      apiFetch<void>(`/api/item-category/${category.id}`, { method: 'DELETE' }),
    success: (_result, category) => `${category.name} deleted.`,
    invalidates: [itemCategoriesKey],
  });

  const columns: DataViewColumn<ItemCategory>[] = [
    {
      title: 'Category',
      dataIndex: 'name',
      // The heading on a phone: the indent, the name and the badges are how a row is identified.
      primary: true,
      render: (_, row) => (
        <div
          className="flex items-center gap-2"
          // The tree, drawn. 18px a level is enough to read as nesting without pushing deep
          // categories off the side of a narrow screen.
          style={{ paddingInlineStart: row.depth * 18 }}
        >
          {row.depth > 0 && <span className="text-default">&#8627;</span>}
          {/* The picture its chip will carry, so a missing one is noticed here, where it is fixed. */}
          <GarmentImage url={row.imageUrl} alt={row.name} size="xs" />
          <span className="font-medium text-gray-900">{row.name}</span>
          {/*
            Only the branch-local rows are marked. Shared is the ordinary case and badging every
            row would be noise — what an operator needs to spot is the exception, the category that
            is theirs alone and not part of the company tree.
          */}
          {!row.isShared && <StatusBadge tone="brand">This branch</StatusBadge>}
          {row.isAccessory && <StatusBadge tone="neutral">Accessory</StatusBadge>}
          {!row.isActive && <StatusBadge tone="warning">Inactive</StatusBadge>}
        </div>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      width: 170,
      render: (_, row) => <span className="font-mono-nums font-semibold">{row.code}</span>,
    },
    {
      title: 'Turnaround',
      dataIndex: 'effectiveTurnaroundBufferDays',
      width: 150,
      align: 'right',
      render: (_, row) => <TurnaroundCell category={row} />,
    },
    {
      title: 'HSN / SAC',
      key: 'gst',
      width: 130,
      responsive: ['xl'],
      render: (_, row) => (
        <span className="font-mono-nums text-[12px] text-default">
          {row.defaultHsnCode ?? '—'} / {row.defaultSacCode ?? '—'}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="ItemCategory:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', category: row })}
              />
            )}
          </WriteAction>

          <RecordAction
            session={session}
            required="ItemCategory:Delete"
            forbidden={
              row.childCount > 0
                ? `Has ${row.childCount} sub-categor${row.childCount === 1 ? 'y' : 'ies'}`
                : undefined
            }
          >
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this category?"
                description="Items already in it would be left without one."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  // A full page rather than a drawer — the rule settled on 2026-09-08 — and it replaces the list
  // rather than sitting over it, as StockIntakePage does on the rail.
  if (editing) {
    return (
      <CategoryPage
        session={session}
        category={editing.mode === 'edit' ? editing.category : undefined}
        categories={categories.data ?? []}
        onClose={() => setEditing(null)}
      />
    );
  }

  if (categories.isError) {
    return <ApiErrorAlert error={categories.error} />;
  }

  return (
    <>
      <PageHeading
        title="Categories"
        subtitle="What kind of thing an item is. Drives cleaning turnaround, package slots and damage rates."
        actions={
          <>
            <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default">
              <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
              Show inactive
            </label>

            <WriteAction session={session} required="ItemCategory:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setEditing({ mode: 'create' })}
                >
                  New category
                </Button>
              )}
            </WriteAction>
          </>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:pb-1">
        <DataView<ItemCategory>
          columns={columns}
          dataSource={categories.data ?? []}
          loading={categories.isLoading}
          empty={
            <EmptyState
              icon="icon icon-layers"
              title="No categories at this branch"
              // Naming the branch matters here: shared categories show everywhere, so an empty
              // list at one branch and a full one at the next is correct behaviour rather than a
              // fault, and this is the sentence that stops it being reported as one.
              hint={`Nothing shared company-wide, and nothing local to ${
                session.branch?.code ?? 'this branch'
              }. Categories marked "shared" appear at every branch.`}
            />
          }
        />
      </Card>
    </>
  );
}

/**
 * The turnaround buffer, showing where the number came from.
 *
 * "2 days (inherited)" and "2 days" mean different things to whoever is about to change it —
 * editing the first one has no effect unless they set an override, and a screen that rendered
 * both as "2" would make that invisible.
 */
function TurnaroundCell({ category }: { category: ItemCategory }) {
  const days = category.effectiveTurnaroundBufferDays;
  const label = `${days} day${days === 1 ? '' : 's'}`;

  if (category.turnaroundBufferDays !== null) {
    return <span className="font-mono-nums">{label}</span>;
  }

  return (
    <span className="font-mono-nums text-default">
      {label}
      <span className="ms-1.5 text-[11px]">inherited</span>
    </span>
  );
}
